import React from 'react';
import { toast } from 'react-toastify';
import { getProjectById, updateProject } from '../../../../../helpers/project-helper';

const normalizeDateForInput = (value) => {
    if (!value) return '';

    const raw = String(value);
    if (raw.length >= 10 && raw[4] === '-' && raw[7] === '-') return raw.slice(0, 10);

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
};

export const useProjectDetailScreen = ({ token, id, navigate, location }) => {
    const [project, setProject] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [activeTab, setActiveTab] = React.useState('general');
    const [isSaving, setIsSaving] = React.useState(false);

    const [form, setForm] = React.useState({
        name: '',
        status: 'ACTIVE',
        startDate: '',
        endDate: '',
    });

    const tabs = React.useMemo(() => ([
        { key: 'general', label: 'General' },
        { key: 'ticket-catalog', label: 'Categorías' },
        { key: 'schedule', label: 'Horario' },
        { key: 'sla', label: 'SLA' },
        { key: 'groups', label: 'Grupos' },
        { key: 'custom-fields', label: 'Campos' },
        { key: 'escalations', label: 'Escalaciones' },
    ]), []);

    const statusClassMap = {
        ACTIVE: 'bg-soft-success text-success',
        SUSPENDED: 'bg-soft-warning text-warning',
        INACTIVE: 'bg-soft-danger text-danger',
    };

    const formatDate = React.useCallback((isoDate) => {
        if (!isoDate) return '-';
        const date = new Date(isoDate);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }, []);

    React.useEffect(() => {
        const fetchProject = async () => {
            if (!token || !id) {
                setError('No se pudo obtener el proyecto.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getProjectById(token, id);

            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener el proyecto.');
                setProject(null);
                setIsLoading(false);
                return;
            }

            setProject(resp.data || null);

            const fetchedProjectName = resp?.data?.name;
            const currentProjectName = location.state?.projectName;

            if (fetchedProjectName && fetchedProjectName !== currentProjectName) {
                navigate(location.pathname, {
                    replace: true,
                    state: {
                        ...(location.state || {}),
                        projectName: fetchedProjectName,
                    },
                });
            }

            setIsLoading(false);
        };

        fetchProject();
    }, [token, id, navigate, location.pathname, location.state]);

    React.useEffect(() => {
        if (!project) return;

        setForm({
            name: project.name || '',
            status: project.status || 'ACTIVE',
            startDate: normalizeDateForInput(project.startDate),
            endDate: normalizeDateForInput(project.endDate),
        });
    }, [project]);

    const handleInputChange = React.useCallback((event) => {
        const { name, value } = event.target;
        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    }, []);

    const handleSave = React.useCallback(async () => {
        if (!token || !id || isSaving) return;

        setIsSaving(true);
        setError('');
        const savingToastId = toast.loading('Guardando cambios del proyecto...');

        const payload = {
            name: form.name,
            status: form.status,
            startDate: form.startDate || undefined,
            endDate: form.endDate || undefined,
        };

        const resp = await updateProject(token, id, payload);

        if (!resp?.success) {
            const message = resp?.message || 'No se pudo actualizar el proyecto.';
            setError(message);
            toast.update(savingToastId, {
                render: message,
                type: 'error',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
            setIsSaving(false);
            return;
        }

        setProject(resp.data || null);
        setIsSaving(false);
        toast.update(savingToastId, {
            render: resp?.message || 'Proyecto actualizado correctamente.',
            type: 'success',
            isLoading: false,
            autoClose: 2500,
            closeOnClick: true,
        });
    }, [token, id, isSaving, form]);

    const customerRef = project?.customer;
    const customerId = typeof customerRef === 'object' ? customerRef?._id : customerRef;
    const customerName = typeof customerRef === 'object'
        ? (customerRef?.name || customerRef?.code || customerRef?._id)
        : customerRef;

    return {
        project,
        isLoading,
        error,
        activeTab,
        isSaving,
        form,
        tabs,
        statusClassMap,
        customerId,
        customerName,
        formatDate,
        setActiveTab,
        handleInputChange,
        handleSave,
    };
};
